import _ from 'lodash';

import config from '../config';
import trainTime from '../lib/TrainTime';
import { TicketTypes, GroupSavePassengers } from '../constants/AppConstants';
import APIClient from '../lib/APIClient';
import Service from '../records/Service';
import PlusBus from '../records/PlusBus';
import { resolve, resolveAll } from '../helpers/LinkMapper';
import PromoCodeStore from '../stores/PromoCodeStore';
import OrderStore from '../stores/OrderStore';

export function serialiseRailcards (search) {
    return search.railcards.flatMap(r => _.times(r.quantity, () => r.code)).toJS();
}

const getPromoCode = (searchRecord) => {
    const currentOrder = OrderStore.getCurrent();

    const promo = !searchRecord.promoCode.isEmpty() ? searchRecord.promoCode : PromoCodeStore.getActive();
    if (!currentOrder || !promo) {
        return null;
    }

    const isThisPromoCodeHasAlreadyApplied = currentOrder.checkIfThisPromoCodeHasAlreadyApplied(promo.promoCode);

    if (promo && !isThisPromoCodeHasAlreadyApplied) {
        return promo.promoCode;
    }
    return null;
};

function requestSearch (searchRecord, user, delayRepayFiltering) {
    const url = '/jp/journey-plan';

    let requestBody = {
        origin: searchRecord.origin.nlc,
        destination: searchRecord.destination.nlc,
        outward: searchRecord.outboundDateTime.toRange({ truncateStart: trainTime() }),
        openReturn: searchRecord.ticketType === TicketTypes.OPEN_RETURN,
        adults: searchRecord.adults,
        children: searchRecord.children,
        disableGroupSavings: !searchRecord.groupSave,
        numJourneys: searchRecord.numJourneys,
        showCheapest: searchRecord.showCheapest,
        doRealTime: searchRecord.realTime,
        keepAllZoneFares: false,
        filterFares: true,
        delayRepayFiltering,
        isAmending: searchRecord.isAmending,
        orderId: searchRecord.orderId,
        preferences: {
            loadEarlier: searchRecord.searchPreferences.loadEarlier,
        }
    };

    if (user && user.id) {
        requestBody.customerId = user.id;
    }

    const serviceProviders = config.get('serviceProviders'),
        alwaysFilterByServiceProviders = config.get('alwaysFilterByServiceProviders');

    if (serviceProviders && (alwaysFilterByServiceProviders || searchRecord.tocSpecific)) {
        requestBody.serviceProviders = serviceProviders;
    }

    const promoCode = getPromoCode(searchRecord);
    if (promoCode) {
        requestBody.promoCode = promoCode;
    }

    const railcards = serialiseRailcards(searchRecord);
    if (railcards.length > 0) {
        requestBody.railcards = railcards;
    }

    let preferences = {};

    if (searchRecord.via.nlc || searchRecord.avoid.nlc) {
        preferences.via = searchRecord.via.nlc ? [ searchRecord.via.nlc ] : null;
        preferences.avoid = searchRecord.avoid.nlc ? [ searchRecord.avoid.nlc ] : null;
    }

    if (searchRecord.smartcard && user) {
        preferences.smartCard = true;
        if (user.isChild()) {
            Object.assign(requestBody, { adults: 0, children: 1 });
        } else {
            Object.assign(requestBody, { adults: 1, children: 0 });
        }
    }

    if (searchRecord.ticketType === TicketTypes.RETURN) {
        let minReturnStart = searchRecord.outboundDateTime.getStart().add(15, 'minutes');
        let maxOutwardEnd = searchRecord.returnDateTime.getEnd().subtract(15, 'minutes');
        requestBody.outward = searchRecord.outboundDateTime.toRange({ truncateEnd: maxOutwardEnd });
        requestBody.return = searchRecord.returnDateTime.toRange({ truncateStart: minReturnStart });
    }

    if (!_.isEmpty(preferences)) { requestBody.preferences = preferences; }

    return APIClient.post(url, requestBody);
}

function qualifiesForGroupSave ({ adults, children }) {
    const passengerCount = adults + children;
    const { MIN, MAX } = GroupSavePassengers;
    return _.inRange(passengerCount, MIN, MAX + 1);
}

function resolveResponse (responseBody) {
    const outward = _.result(responseBody.result, 'outward', []);
    const inward = _.result(responseBody.result, 'return', []);
    const hasServiceProvider = _.result(responseBody.result, 'useServiceProvider', false);

    const outServices = resolveAll(outward).as(Service);
    const inwardServices = resolveAll(inward).as(Service);

    return { outward: outServices, inward: inwardServices, hasServiceProvider };
}

function mergeCheapest (left, right) {
    let cheapest = null;
    if (!_.isArray(left) && _.isObject(left) && _.result(left, 'totalPrice')) {
        cheapest = left;
    }

    if (
        !_.isArray(right)
        && _.isObject(right)
        && _.result(right, 'totalPrice')
        && parseInt(_.result(cheapest, 'totalPrice')) > parseInt(_.result(right, 'totalPrice'))
    ) {
        cheapest = right;
    }

    return cheapest;
}

function mergeFares (left, right) {
    return _.mergeWith(left, right, (l, r) => { // eslint-disable-line  consistent-return
        if (Array.isArray(l) || Array.isArray(r)) {
            return _.union(l, r);
        }
        return mergeCheapest(l, r);
    });
}

function mergeServices (left, right) {
    const combinedServices = _.compact([].concat(left, right));
    const journeyFares = combinedServices.reduce((acc, { journey, fares }) => {
        if (journey in acc) {
            acc[journey] = mergeFares(acc[journey], fares);
        } else {
            acc[journey] = fares;
        }
        return acc;
    }, {});
    return Object.keys(journeyFares).reduce((services, journey) => {
        services.push({
            journey,
            fares: journeyFares[journey]
        });
        return services;
    }, []);
}

function mergeResponses (searchResponse, groupSaveResponse) {
    if (!groupSaveResponse) {
        return searchResponse;
    }

    const sources = [searchResponse, groupSaveResponse];

    return {
        result: {
            outward: mergeServices(..._.map(sources, 'result.outward')),
            return: mergeServices(..._.map(sources, 'result.return')),
            useServiceProvider: _.some(_.map(sources, 'result.useServiceProvider'))
        }
    };
}

function findCheapestOpenReturn (serviceList) {
    return serviceList.outward.sortBy(({ fares }) => fares.cheapest.return.totalPrice).first();
}

const JourneyPlanService = {
    search (options) {
        const { delayRepayFiltering = false, search, user } = options;
        let searches = [ requestSearch(search, user, delayRepayFiltering) ];

        if (qualifiesForGroupSave(search)) {
            let groupSaveSearch = search.set('groupSave', true);
            searches.push(requestSearch(groupSaveSearch, user));
        }

        return Promise.all(searches).then((responses) => {
            return resolveResponse(mergeResponses(...responses));
        });
    },

    async getCheapestOpenReturn (searchRecord, user) {
        try {
            const serviceList = await this.search(searchRecord, user);
            const cheapestOpenReturn = findCheapestOpenReturn(serviceList);

            if (!cheapestOpenReturn) {
                throw new Error('No open return tickets available for this trip');
            }

            return cheapestOpenReturn.fares.cheapest.return.totalPrice;
        } catch (error) {
            console.warn(error.message);
            return null;
        }
    },

    plusBus (trip) {
        const { search } = trip;
        const url = '/jp/plusbus';
        let requestBody = {
            adults: search.adults,
            children: search.children
        };

        requestBody.outward = {
            origin: search.getIn(['origin', 'id']),
            destination: search.getIn(['destination', 'id']),
            date: search.outboundDateTime.datetime.format('YYYY-MM-DD')
        };

        if (trip.isReturn()) {
            requestBody.return = {
                origin: search.getIn(['destination', 'id']),
                destination: search.getIn(['origin', 'id']),
                date: search.returnDateTime.datetime.format('YYYY-MM-DD')
            };
        }

        const railcards = serialiseRailcards(search);
        if (railcards.length > 0) {
            requestBody.railcards = railcards;
        }

        return APIClient.post(url, requestBody).then(response => {
            return resolve(response.result).as(PlusBus);
        });
    },

    validatePromoCode(customerId, promoCode) {
        const customerIdParam = customerId ? `&customerId=${customerId}` : '';
        return APIClient.get(`/jp/promotional-discounts?channel=web&promoCode=${promoCode}${customerIdParam}`);
    }
};

export default JourneyPlanService;



// WEBPACK FOOTER //
// ./src/services/JourneyPlanService.js